import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { MOBILE_TOGGLE_ICON_SIZE_PX } from "@/constants/app";
import useScrollBehavior from "@/hooks/useScrollBehavior";
import DesktopNav from "@/components/nav/DesktopNav";
import MobileMenu from "@/components/nav/MobileMenu";

const LINKS = [
  { to: "/", label: "Home", id: NAV.home, end: true },
  { to: "/destinations", label: "Destinations", id: NAV.destinations },
  { to: "/about", label: "Atelier", id: NAV.about },
  { to: "/journal", label: "Journal", id: NAV.journal },
  { to: "/contact", label: "Contact", id: NAV.contact },
];

function getHeaderClass(scrolled, onLanding) {
  if (scrolled) return "bg-ivory/90 backdrop-blur-xl border-b border-line-light";
  if (onLanding) return "bg-transparent";
  return "bg-ivory/80 backdrop-blur-md";
}

export default function Navbar() {
  const scrolled = useScrollBehavior();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile menu whenever the route changes. `setOpen` is stable
  // (returned from useState) and intentionally omitted from the deps array.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onLanding = pathname === "/" && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${getHeaderClass(scrolled, onLanding)}`}
    >
      <div className="w-full pl-6 pr-6 md:pl-10 md:pr-10 flex items-center justify-between py-4 md:py-5">
        <Link
          to="/"
          data-testid={NAV.brand}
          className={`flex items-center gap-2 md:gap-3 shrink-0 ${onLanding ? "text-ivory" : "text-indigo_deep"}`}
        >
          <img
            src="/brand/indiora-mark.png"
            alt="Indiora Journeys"
            className={`h-14 sm:h-20 md:h-28 lg:h-36 xl:h-40 w-auto object-contain transition-all duration-500 ${
              onLanding ? "drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)]" : ""
            }`}
          />
          <span className={`hidden lg:inline font-serif lg:text-[40px] xl:text-[48px] 2xl:text-[52px] tracking-[0.08em] uppercase font-semibold whitespace-nowrap leading-none ${onLanding ? "text-ivory" : "text-indigo_deep"}`}>
            Indiora Journeys
          </span>
        </Link>

        <DesktopNav links={LINKS} onLanding={onLanding} />

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 ${onLanding ? "text-ivory" : "text-ink"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={MOBILE_TOGGLE_ICON_SIZE_PX} /> : <Menu size={MOBILE_TOGGLE_ICON_SIZE_PX} />}
        </button>
      </div>

      {open && <MobileMenu links={LINKS} />}
    </header>
  );
}
